import isEqual from 'lodash/isEqual';

import { speedStoreActions, useSpeedStore } from 'modules/speed/store/speedStore';

import { queueSettingsChange } from '../service';
import { SharedUserSettingsEnum, UserSettingsEnum, useSettingsStore } from '../store';

export function subscribe() {
  const remoteSettingsSyncChangesToSkip = new Set<number | boolean>();
  const localSpeedSyncChangesToSkip = new Set<number | boolean>();

  // remote settings store change, set local app speed
  const settingsSubscription = useSettingsStore.subscribe((state, prevState) => {
    const currentSpeed = Math.round(state.userSettings.settings[SharedUserSettingsEnum.PLAYBACK_SPEED] || 0);
    const prevSpeed = Math.round(prevState.userSettings.settings[SharedUserSettingsEnum.PLAYBACK_SPEED] || 0);

    // remote current speed change
    if (currentSpeed && !isEqual(currentSpeed, prevSpeed)) {
      if (!localSpeedSyncChangesToSkip.has(currentSpeed)) {
        remoteSettingsSyncChangesToSkip.add(currentSpeed);
        speedStoreActions.setSpeedInWPM(currentSpeed, 'settingsSync');
      }

      localSpeedSyncChangesToSkip.clear();
    }

    // remote automatic increase speed mode change
    const currentIsInAutomaticIncreaseSpeedMode = state.userSettings.settings[SharedUserSettingsEnum.AUTO_SPEED_UP];
    const prevIsInAutomaticIncreaseSpeedMode = prevState.userSettings.settings[SharedUserSettingsEnum.AUTO_SPEED_UP];

    if (currentIsInAutomaticIncreaseSpeedMode !== undefined && currentIsInAutomaticIncreaseSpeedMode !== prevIsInAutomaticIncreaseSpeedMode) {
      if (!localSpeedSyncChangesToSkip.has(currentIsInAutomaticIncreaseSpeedMode)) {
        remoteSettingsSyncChangesToSkip.add(currentIsInAutomaticIncreaseSpeedMode);
        speedStoreActions.onAutomaticIncreaseSpeedPreferenceChange(currentIsInAutomaticIncreaseSpeedMode);
      }

      localSpeedSyncChangesToSkip.clear();
    }
  });

  // local speed store change, set remote settings store speed
  const speedSubscription = useSpeedStore.subscribe((state, prevState) => {
    const currentSpeed = Math.round(state.currentSpeedInWPM);
    const prevLastSpeed = Math.round(prevState.currentSpeedInWPM);

    const diff = Math.abs(currentSpeed - prevLastSpeed);

    // local speed change
    if (currentSpeed && diff >= 1) {
      if (!remoteSettingsSyncChangesToSkip.has(currentSpeed)) {
        localSpeedSyncChangesToSkip.add(currentSpeed);

        queueSettingsChange({
          [UserSettingsEnum.PLAYBACK_SPEED]: currentSpeed
        });
      }

      remoteSettingsSyncChangesToSkip.clear();
    }

    const currentIsInAutomaticIncreaseSpeedMode = state.isInAutomaticIncreaseSpeedMode;
    const prevIsInAutomaticIncreaseSpeedMode = prevState.isInAutomaticIncreaseSpeedMode;

    // local automatic increase speed mode change
    if (currentIsInAutomaticIncreaseSpeedMode !== undefined && currentIsInAutomaticIncreaseSpeedMode !== prevIsInAutomaticIncreaseSpeedMode) {
      if (!remoteSettingsSyncChangesToSkip.has(currentIsInAutomaticIncreaseSpeedMode)) {
        localSpeedSyncChangesToSkip.add(currentIsInAutomaticIncreaseSpeedMode);

        queueSettingsChange({
          [UserSettingsEnum.AUTO_SPEED_UP]: currentIsInAutomaticIncreaseSpeedMode
        });
      }

      remoteSettingsSyncChangesToSkip.clear();
    }
  });

  // cleanup
  return () => {
    settingsSubscription?.();
    speedSubscription?.();
  };
}
